import React, { useState } from 'react'
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Close as CloseIcon, KeyboardArrowDown } from '@mui/icons-material'
import { useNavigate } from 'react-router'
import ExploreMenu from '../../../components/explore-menu'
import { PaidConsumerSearchBar } from '../search/paid-consumer-search-bar'
import { PaidConsumerNavLogo } from './paid-consumer-nav-logo'
import { useFormFactor } from '../../../hooks'
import MobileLatestArticlesCarousel from '../../../components/mobile-latest-articles-carousel'
import mobileBackground from './mobile-background.svg'
import usePaidConsumerActions from '../hooks/usePaidConsumerActions'

const useStyles = makeStyles(({ spacing, palette }) => ({
  // Mobile Drawer
  mobileDrawer: {
    '& .MuiDrawer-paper': {
      width: '100vw',
      height: '100vh',
      backgroundColor: '#5D5BED',
      display: 'flex',
      flexDirection: 'column',
      overflowX: 'hidden'
    }
  },
  drawerContent: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    zIndex: 1,
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch'
  },
  drawerHeader: {
    padding: spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#5D5BED',
    color: palette.common.white,
    position: 'sticky',
    top: 0,
    zIndex: 2
  },
  // Tablet-specific header layout: Search (30%) | Logo | Close
  drawerHeaderTablet: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing(2),
    width: '100%'
  },
  drawerHeaderContent: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(2),
    flex: 1
  },
  drawerSearchContainer: {
    flex: 1,
    '& .MuiPaper-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      border: 'none',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)'
      }
    },
    '& .MuiOutlinedInput-input': {
      color: palette.common.black,
      '&::placeholder': {
        color: 'rgba(0, 0, 0, 1)',
        opacity: 1
      }
    },
    '& .MuiSvgIcon-root': {
      color: palette.common.black
    }
  },
  // Tablet: constrain search to ~40% of header width
  drawerSearchContainerTablet: {
    flex: '0 0 30%',
    maxWidth: '30%'
  },
  drawerCloseButton: {
    color: palette.common.black,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)'
    }
  },
  drawerTitle: {
    fontWeight: 600,
    color: palette.text.primary
  },
  drawerMenuItem: {
    padding: `${spacing(1.5)} ${spacing(2)}`,
    color: palette.common.white,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)'
    },
    '& .MuiListItemIcon-root': {
      color: palette.common.white,
      '& svg': {
        filter: 'brightness(0) invert(1)'
      }
    },
    // Bring the leading icon closer to the text on mobile drawer items
    '& .MuiListItemIcon-root:first-of-type': {
      minWidth: 36,
      marginRight: spacing(1)
    },
    // Ensure the text does not expand and push the trailing icon to the far right
    '& .MuiListItemText-root': {
      flex: '0 0 auto',
      marginRight: spacing(0.5)
    },
    '& .MuiListItemText-primary': {
      color: palette.common.white
    }
  },
  drawerMenuItemArrow: {
    color: palette.common.black
  },
  // Decorative background between the menu list and latest articles carousel
  drawerBetweenBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 0
  },
  drawerBetweenBackgroundImage: {
    position: 'absolute',
    right: spacing(-1),
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'block',
    width: '75%',
    maxWidth: 450,
    height: 'auto',
    objectFit: 'contain',
    opacity: 0.5,
    zIndex: 0,
    pointerEvents: 'none'
  }
}))

export const PaidConsumerMobileDrawer = ({ open, onClose }) => {
  const classes = useStyles()
  const navigate = useNavigate()
  const { isSmallTablet, isLargeTablet } = useFormFactor()
  const isTablet = isSmallTablet || isLargeTablet
  const [showExplore, setShowExplore] = useState(false)

  const handleExploreToggle = () => {
    setShowExplore(show => !show)
    // Don't close drawer immediately - let user interact with explore menu
  }

  const handleExploreMenuClose = () => {
    setShowExplore(false)
    onClose()
  }

  const { getActionData, handleMCoachClick } = usePaidConsumerActions({
    onClose,
    onExploreToggle: handleExploreToggle
  })

  // Get context based on device type
  const context = isTablet ? 'tablet' : 'mobile'
  const menuItems = getActionData(context)

  // Handle navigation for items that need it
  const handleItemClick = (item) => {
    if (item.to) {
      navigate(item.to)
      onClose()
    } else if (item.onClick) {
      if (item.testId === 'mcoach') {
        handleMCoachClick(null, 'paid-consumer-mobile-nav')
      } else {
        item.onClick()
      }
    }
  }

  return (
    <>
      <Drawer
        anchor='left'
        open={open}
        onClose={onClose}
        className={classes.mobileDrawer}
      >
        <Box className={classes.drawerContent}>
          <Box className={classes.drawerHeader}>
            {isTablet
              ? (
                // Tablet: single row with equal spacing between Search, Logo, Close
                <Box className={classes.drawerHeaderTablet}>
                  <Box className={`${classes.drawerSearchContainer} ${classes.drawerSearchContainerTablet}`}>
                    <PaidConsumerSearchBar onDrawerClose={onClose} />
                  </Box>
                  <PaidConsumerNavLogo onClose={onClose} white />
                  <IconButton
                    onClick={onClose}
                    className={classes.drawerCloseButton}
                    data-test='paid-consumer-mobile-drawer-close'
                    aria-label='Close navigation drawer'
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                )
              : (
                // Mobile: Logo | Search | Close (existing layout)
                <>
                  <Box className={classes.drawerHeaderContent}>
                    <PaidConsumerNavLogo onClose={onClose} isMobile white />
                    <Box className={classes.drawerSearchContainer}>
                      <PaidConsumerSearchBar onDrawerClose={onClose} />
                    </Box>
                  </Box>
                  <IconButton
                    onClick={onClose}
                    className={classes.drawerCloseButton}
                    data-test='paid-consumer-mobile-drawer-close'
                    aria-label='Close navigation drawer'
                  >
                    <CloseIcon />
                  </IconButton>
                </>
                )}
          </Box>

          <List>
            {Object.values(menuItems).map((item) => {
              const IconComponent = item.icon
              return (
                <ListItem key={item.label} disablePadding>
                  <ListItemButton
                    onClick={() => handleItemClick(item)}
                    className={classes.drawerMenuItem}
                    data-test={`paid-consumer-mobile-drawer-${item.testId}`}
                  >
                    <ListItemIcon>
                      <IconComponent />
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                    {item.testId === 'content-hub' && <KeyboardArrowDown className={classes.drawerMenuItemArrow} />}
                  </ListItemButton>
                </ListItem>
              )
            })}
          </List>

          {/* Decorative background between menu and carousel (absolute, non-interactive) */}
          <Box className={classes.drawerBetweenBackground} aria-hidden='true'>
            <img src={mobileBackground} alt='' className={classes.drawerBetweenBackgroundImage} />
          </Box>

          {/* Latest Articles Carousel - shown below menu items */}
          <Box sx={{ position: 'relative', zIndex: 1, mt: 1, mb: 2 }}>
            <MobileLatestArticlesCarousel onNavigate={onClose} />
          </Box>
        </Box>

        {/* Explore Menu - same behavior as Desktop Navigation */}
        <ExploreMenu open={showExplore} onClose={handleExploreMenuClose} />
      </Drawer>
    </>
  )
}
